import { useState } from "react";

export interface TagChipsProps {
  tags: string[];
  /** When true, shows remove buttons and an add-tag input. */
  editable?: boolean;
  onAdd?: (tag: string) => void | Promise<void>;
  onRemove?: (tag: string) => void | Promise<void>;
}

/** Tag pills — display-only by default, editable (add/remove) when asked. */
export function TagChips({ tags, editable = false, onAdd, onRemove }: TagChipsProps) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    const tag = draft.trim().toLowerCase();
    if (!tag || !onAdd || busy) return;
    if (tags.includes(tag)) {
      setDraft("");
      return;
    }
    setBusy(true);
    try {
      await onAdd(tag);
      setDraft("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chips">
      {tags.map((t) => (
        <span className="chip" key={t}>
          {t}
          {editable && onRemove && (
            <button
              type="button"
              className="chip-remove"
              onClick={() => void onRemove(t)}
              aria-label={`Remove tag ${t}`}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {tags.length === 0 && !editable && <span className="muted">—</span>}
      {editable && (
        <span className="chip-add">
          <input
            value={draft}
            placeholder="add tag"
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submit();
              }
            }}
            aria-label="New tag"
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void submit()}
            disabled={busy || !draft.trim()}
          >
            Add
          </button>
        </span>
      )}
    </div>
  );
}

export default TagChips;
